import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { PrimaryTitleGray } from '../../../../app/theme/tokens/colors';
import { InputText } from '../../../../shared/ui/Inputs';
import {
  GreenLabel,
  RedLabel,
  YellowLabel,
} from '../../../../shared/messages/labels';
import { ErrorMessage } from '../../../../shared/messages/messages';
import { Table } from '../../../../shared/ui/table';
import { OrderFlowTable } from '../../../../shared/ui/orderFlowTable';
import { TitledPanel } from '../../../../shared/ui/dashboard/titledPanel/titledPanel';
import { LargeTile } from '../../../../shared/ui/grid/gridsystem';
import { BasicPagination } from '../../../../shared/ui/pagination/basic';
import { usePortalStore } from '../../../../stores/portalStore';
import { usePatientsStore } from '../../store/patientsStore';
import {
  displayOrDash,
  formatDocumentOrDash,
} from '../../../../services/patients/patientsService';
import { formatCPF } from '../../../../utils/format';
import { MdOutlinePersonAddDisabled, MdOutlinePersonAdd } from 'react-icons/md';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { Tooltip } from '../../../../shared/ui/Tooltip';
import { PatientName } from '../../../../shared/domain/patient/PatientName';
import { Spinner } from '../../../../shared/ui/Spinners';
import { useCompaniesCatalog } from '../../../../hooks/useCompaniesCatalog';

export const PatientsList = ({
  title,
  onlyActives,
  height,
  subtitle,
  showDeletePopup,
  showEditPopup,
  showDivisionPopup,
  readOnly,
  header,
  fields,
  onRowSelected,
  newClientAction,
  topSearchRight,
  disableSearchWhileLoading = false,
  resetSearchOnMount = false,
  useOrderFlowTable = false,
  // Pesquisa só ao clicar em "Pesquisar" ou apertar Enter, sem busca automática ao digitar.
  manualSearch = false,
}) => {
  const patients = usePatientsStore((state) => state.patients);
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1224px)' });
  const currentUser = usePortalStore((state) => state.currentUser);
  const error = usePatientsStore((state) => state.error);
  const infoMessage = usePatientsStore((state) => state.infoMessage);
  const fetchPatients = usePatientsStore((state) => state.fetchPatients);
  const searchTerm = usePatientsStore((state) => state.searchTerm);
  const setSearchTerm = usePatientsStore((state) => state.setSearchTerm);
  const [autoFilter, setAutoFilter] = useState('');
  const [searchRequest, setSearchRequest] = useState(0);
  const fetchTotalItems = usePatientsStore((state) => state.fetchTotalItems);
  const clearPatientsList = usePatientsStore(
    (state) => state.clearPatientsList
  );
  const autoFilterTimeoutRef = useRef(null);
  const patientsRequestRef = useRef(0);

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const tableAreaRef = useRef(null);
  const paginationRef = useRef(null);
  const [autoTableHeight, setAutoTableHeight] = useState(300);

  const { resolveCompanyName } = useCompaniesCatalog();

  const recalcAutoTableHeight = () => {
    if (!tableAreaRef.current) return;

    const tableTop = tableAreaRef.current.getBoundingClientRect().top;
    const paginationHeight = paginationRef.current?.offsetHeight ?? 46;
    const paginationGap = 10;
    const outerBottomMargin = 24;

    const available =
      window.innerHeight -
      tableTop -
      paginationHeight -
      paginationGap -
      outerBottomMargin;

    setAutoTableHeight(Math.max(180, Math.floor(available)));
  };

  useEffect(() => {
    clearPatientsList();
    if (resetSearchOnMount) {
      setSearchTerm('');
      setAutoFilter('');
      setCurrentPage(0);
    }

    return () => {
      patientsRequestRef.current += 1;
      if (resetSearchOnMount) {
        setSearchTerm('');
        clearPatientsList();
      }
    };
  }, [clearPatientsList, resetSearchOnMount, setSearchTerm]);

  useEffect(() => {
    const requestId = ++patientsRequestRef.current;
    (async () => {
      try {
        setIsLoading(true);
        setCurrentPage(0);
        await fetchPatients(
          currentUser.cnpj,
          autoFilter,
          null,
          null,
          onlyActives ? 'Ativo' : null
        );
      } finally {
        if (requestId === patientsRequestRef.current) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      if (requestId === patientsRequestRef.current) {
        patientsRequestRef.current += 1;
      }
    };
  }, [autoFilter, searchRequest]);

  useEffect(() => {
    const id = requestAnimationFrame(recalcAutoTableHeight);
    window.addEventListener('resize', recalcAutoTableHeight);

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', recalcAutoTableHeight);
    };
  }, [patients.length, isLoading, error, fetchTotalItems]);

  useEffect(() => {
    if (manualSearch) return;

    if (autoFilterTimeoutRef.current) {
      clearTimeout(autoFilterTimeoutRef.current);
    }

    autoFilterTimeoutRef.current = setTimeout(() => {
      setAutoFilter(searchTerm);
    }, 700);

    return () => {
      if (autoFilterTimeoutRef.current) {
        clearTimeout(autoFilterTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  const submitSearch = () => {
    setAutoFilter(searchTerm);
    setSearchRequest((current) => current + 1);
  };

  const paginate = (e) => {
    const requestId = ++patientsRequestRef.current;
    (async () => {
      const nextPage = parseInt(String(e.page ?? 0), 10);
      const nextPageSize = parseInt(String(e.pageSize ?? 10), 10);

      try {
        setIsLoading(true);
        setCurrentPage(Number.isFinite(nextPage) ? nextPage : 0);
        setCurrentPageSize(Number.isFinite(nextPageSize) ? nextPageSize : 10);
        await fetchPatients(
          currentUser.cnpj,
          autoFilter,
          Number.isFinite(nextPage) ? nextPage : 0,
          Number.isFinite(nextPageSize) ? nextPageSize : 10,
          onlyActives ? 'Ativo' : null
        );
      } finally {
        if (requestId === patientsRequestRef.current) {
          setIsLoading(false);
        }
      }
    })();
  };

  const isPatientInactive = (status) => status?.toLowerCase() === 'inativo';
  const isPatientRequested = (status) => status?.toLowerCase() === 'solicitado';
  const getPatientActionLabel = (status) =>
    isPatientInactive(status) ? 'Ativar' : 'Inativar';
  const canManagePatients = currentUser.isAdmin || currentUser.isManager;
  const canViewPatients = canManagePatients || currentUser.isWhiteMartins;
  const canChangeDivision = Boolean(
    showDivisionPopup && (currentUser.isAdmin || currentUser.isWhiteMartins)
  );
  const showActionsColumn = !readOnly && canViewPatients;
  const hasActionsHeader =
    Array.isArray(header) &&
    Array.isArray(fields) &&
    header.length === fields.length + 1;
  const effectiveHeader =
    showActionsColumn || !hasActionsHeader ? header : header.slice(1);
  const PatientsTable = useOrderFlowTable ? OrderFlowTable : Table;
  const formatDocument = (value, formatter) => {
    return formatDocumentOrDash(value, formatter, {
      documentType: formatter ? 'cpf' : 'cnpj',
    });
  };

  const renderSearchInput = (style) => {
    if (!manualSearch) {
      return (
        <InputText
          placeholder="Buscar paciente por nome, CPF ou número SAP."
          style={style}
          disabled={disableSearchWhileLoading && isLoading}
          aria-busy={isLoading}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          value={searchTerm}
        ></InputText>
      );
    }

    // Na busca manual a digitação não é bloqueada: com o banco lento, a resposta
    // pode levar até 30 segundos e o usuário precisa poder corrigir o termo.
    return (
      <div style={{ ...style, position: 'relative' }}>
        <InputText
          placeholder="Buscar paciente por nome, CPF ou número SAP."
          style={{ width: '100%', paddingRight: '40px' }}
          aria-busy={isLoading}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          onKeyUp={(e) => {
            if (e.key === 'Enter') submitSearch();
          }}
          value={searchTerm}
        ></InputText>
        <button
          type="button"
          aria-label="Pesquisar paciente"
          title="Pesquisar"
          onClick={submitSearch}
          style={{
            position: 'absolute',
            right: '6px',
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '4px',
            border: 'none',
            background: 'transparent',
            color: PrimaryTitleGray,
            cursor: 'pointer',
          }}
        >
          <SearchIcon fontSize="small" />
        </button>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {topSearchRight ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            gap: '16px',
          }}
        >
          <h3 style={{ color: PrimaryTitleGray, margin: 0 }}>{title}</h3>
          <div style={{ width: isTabletOrMobile ? '100%' : '50%' }}>
            {renderSearchInput({ width: '100%' })}
          </div>
        </div>
      ) : (
        <h3
          style={{ color: PrimaryTitleGray, margin: 0, paddingBottom: '10px' }}
        >
          {title}
        </h3>
      )}
      <TitledPanel
        action={newClientAction ? newClientAction : <></>}
        title={topSearchRight ? '' : 'Pacientes'}
        subtitle={subtitle != '' ? subtitle : ''}
        outlet={
          <div style={{ display: 'block', position: 'relative' }}>
            {!topSearchRight &&
              renderSearchInput({ width: isTabletOrMobile ? '100%' : '50%' })}

            {!isLoading ? (
              <>
                <div
                  ref={tableAreaRef}
                  style={{
                    height: height ? height : `${autoTableHeight}px`,
                    width: '100%',
                    marginTop: '20px',
                    overflow: 'auto',
                  }}
                >
                  {error ? (
                    <ErrorMessage>{`Não foi possível recuperar os registros de pacientes: ${error}`}</ErrorMessage>
                  ) : infoMessage ? (
                    <div
                      style={{
                        width: '100%',
                        minHeight: '140px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        color: PrimaryTitleGray,
                        padding: '24px',
                        boxSizing: 'border-box',
                      }}
                    >
                      {infoMessage}
                    </div>
                  ) : (
                    <PatientsTable
                      onRowSelection={onRowSelected}
                      isRowDisabled={(_, idx) =>
                        isPatientInactive(patients[idx]?.status)
                      }
                      header={effectiveHeader}
                      ariaLabel="Seleção de pacientes"
                      data={patients.map((p, idx) => {
                        const columns = [];
                        if (showActionsColumn) {
                          const canToggleStatus =
                            canManagePatients && !isPatientRequested(p.status);
                          columns.push(
                            <div
                              style={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              {canToggleStatus && (
                                <>
                                  <Tooltip
                                    title={getPatientActionLabel(p.status)}
                                  >
                                    {isPatientInactive(p.status) ? (
                                      <MdOutlinePersonAdd
                                        className="hoverButton"
                                        style={{
                                          cursor: 'pointer',
                                          fontSize: '18px',
                                        }}
                                        onClick={() =>
                                          showDeletePopup(p, p.status)
                                        }
                                      />
                                    ) : (
                                      <MdOutlinePersonAddDisabled
                                        className="hoverButton"
                                        style={{
                                          cursor: 'pointer',
                                          fontSize: '18px',
                                        }}
                                        onClick={() =>
                                          showDeletePopup(p, p.status)
                                        }
                                      />
                                    )}
                                  </Tooltip>
                                  <span> </span>
                                </>
                              )}
                              {canViewPatients && (
                                <Tooltip title="Visualizar">
                                  <VisibilityIcon
                                    className="hoverButton"
                                    style={{
                                      marginLeft: canToggleStatus
                                        ? '20px'
                                        : '0',
                                      cursor: 'pointer',
                                      fontSize: '18px',
                                    }}
                                    onClick={() => {
                                      showEditPopup(idx);
                                    }}
                                  ></VisibilityIcon>
                                </Tooltip>
                              )}
                              {canChangeDivision && (
                                <Tooltip title="Alterar divisão">
                                  <button
                                    type="button"
                                    aria-label={`Alterar divisão de ${p.name}`}
                                    className="hoverButton"
                                    style={{
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      marginLeft: '20px',
                                      padding: 0,
                                      border: 'none',
                                      background: 'transparent',
                                      color: 'inherit',
                                      cursor: 'pointer',
                                    }}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      showDivisionPopup(p);
                                    }}
                                  >
                                    <SwapHorizIcon
                                      style={{ fontSize: '18px' }}
                                    />
                                  </button>
                                </Tooltip>
                              )}
                            </div>
                          );
                        }
                        fields.forEach((f) => {
                          if (f === 'status' && p.status) {
                            columns.push(
                              p.status?.toLowerCase() === 'ativo' ? (
                                <GreenLabel
                                  style={{
                                    width: '80px',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {p.status}
                                </GreenLabel>
                              ) : p.status?.toLowerCase() === 'inativo' ? (
                                <RedLabel
                                  style={{
                                    width: '80px',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {p.status}
                                </RedLabel>
                              ) : (
                                <YellowLabel
                                  style={{
                                    width: '80px',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {p.status}
                                </YellowLabel>
                              )
                            );
                          } else if (f === 'cpf') {
                            columns.push(formatDocument(p.cpf, formatCPF));
                          } else if (f === 'cnpj') {
                            columns.push(formatDocument(p.cnpj));
                          } else if (f === 'company') {
                            columns.push(
                              displayOrDash(
                                resolveCompanyName(
                                  p.cnpj,
                                  p.company ?? p.cnpj ?? ''
                                )
                              )
                            );
                          } else if (f === 'name') {
                            columns.push(
                              <PatientName
                                name={displayOrDash(p.name)}
                                status={p.status}
                                additionalInformation={p.additionalInformation?.map(
                                  (i) => i.id
                                )}
                                onPaymentList={p.onPaymentList}
                                canSeeFullContent={canViewPatients}
                              ></PatientName>
                            );
                          } else {
                            columns.push(
                              displayOrDash(p[f], {
                                zeroAsDash: true,
                                repeatedDigitsAsDash: true,
                              })
                            );
                          }
                        });
                        return columns;
                      })}
                    ></PatientsTable>
                  )}
                </div>
                <div ref={paginationRef} style={{ marginTop: '10px' }}>
                  <BasicPagination
                    qtyItems={fetchTotalItems}
                    page={currentPage}
                    pageSize={currentPageSize}
                    onChange={(e) => paginate(e)}
                  ></BasicPagination>
                </div>
              </>
            ) : (
              <div
                style={{
                  width: '100%',
                  textAlign: 'center',
                  overflow: 'hidden',
                  padding: '200px',
                  boxSizing: 'border-box',
                }}
              >
                <Spinner></Spinner>
              </div>
            )}
          </div>
        }
      ></TitledPanel>
    </div>
  );
};
